import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { 
  ArrowLeft, 
  Loader2, 
  Home, 
  UserRound, 
  Hospital, 
  Ambulance,
  Clock,
  Calendar,
  AlertTriangle,
  Thermometer,
  Heart,
  Activity,
  Pill,
  AlertCircle,
  FileText,
  CheckCircle
} from "lucide-react";

interface TriageSession {
  id: string;
  created_at: string;
  completed_at: string | null;
  recommendation: 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW' | null;
  score: number | null;
  explanation_text: string | null;
  red_flags: boolean | null;
}

interface TriageAnswer {
  question_key: string;
  value: any;
  created_at: string;
}

const AssessmentDetailPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [session, setSession] = useState<TriageSession | null>(null);
  const [answers, setAnswers] = useState<TriageAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!sessionId || !user) return;

      try {
        // Fetch session details
        const { data: sessionData, error: sessionError } = await supabase
          .from("triage_sessions")
          .select("*")
          .eq("id", sessionId)
          .maybeSingle();

        if (sessionError) throw sessionError;
        if (!sessionData) {
          setError("Assessment not found");
          return;
        }

        setSession(sessionData);

        // Fetch answers
        const { data: answersData, error: answersError } = await supabase
          .from("triage_answers")
          .select("question_key, value, created_at")
          .eq("session_id", sessionId)
          .order("created_at", { ascending: true });

        if (answersError) throw answersError;
        setAnswers(answersData || []);
      } catch (err: any) {
        console.error("Error fetching assessment:", err);
        setError(err.message || "Failed to load assessment");
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [sessionId, user]);

  const getRecommendationConfig = (rec: string | null) => {
    switch (rec) {
      case "HOME":
        return { label: "Stay Home & Monitor", icon: Home, color: "success", bg: "bg-success/10 text-success border-success/20" };
      case "PCP":
        return { label: "See Your Doctor", icon: UserRound, color: "primary", bg: "bg-primary/10 text-primary border-primary/20" };
      case "URGENT_CARE":
        return { label: "Visit Urgent Care", icon: Hospital, color: "warning", bg: "bg-warning/10 text-warning border-warning/20" };
      case "ER_NOW":
        return { label: "Go to Emergency Room", icon: Ambulance, color: "destructive", bg: "bg-destructive/10 text-destructive border-destructive/20" };
      default:
        return { label: "Assessment Incomplete", icon: Clock, color: "muted", bg: "bg-muted/10 text-muted-foreground border-muted/20" };
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract data from the most recent answer (which contains all triage data)
  const getTriageData = () => {
    if (answers.length === 0) return null;
    
    // Find the answer with the most complete data
    const latestAnswer = answers[answers.length - 1];
    return latestAnswer?.value || null;
  };

  const triageData = getTriageData();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="health-card">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">
                {error || "Assessment not found"}
              </h2>
              <p className="text-muted-foreground mb-4">
                This assessment may not exist or you don't have permission to view it.
              </p>
              <Button onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const recConfig = getRecommendationConfig(session.recommendation);
  const RecIcon = recConfig.icon;

  return (
    <>
      <Helmet>
        <title>Assessment Details - CheckFirstCare</title>
        <meta name="description" content="View your health assessment details and recommendations." />
      </Helmet>

      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          {/* Recommendation Card */}
          <Card className={`health-card mb-6 border-2 ${recConfig.bg}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${recConfig.bg}`}>
                  <RecIcon className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-foreground">{recConfig.label}</h1>
                  {session.explanation_text && (
                    <p className="text-muted-foreground mt-1">{session.explanation_text}</p>
                  )}
                </div>
                {session.completed_at ? (
                  <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed
                  </Badge>
                ) : (
                  <Badge variant="secondary">Incomplete</Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assessment Info */}
          <Card className="health-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5" />
                Assessment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Started</span>
                <span className="font-medium">{formatDate(session.created_at)}</span>
              </div>
              {session.completed_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span className="font-medium">{formatDate(session.completed_at)}</span>
                </div>
              )}
              {session.score !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Severity Score</span>
                  <span className="font-medium">{session.score}/100</span>
                </div>
              )}
              {session.red_flags && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-lg mt-4">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                  <span className="text-destructive font-medium">Red flags were identified</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Triage Data Details */}
          {triageData && (
            <>
              {/* Chief Complaint */}
              {triageData.chief_complaint && (
                <Card className="health-card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5" />
                      Chief Complaint
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground">{triageData.chief_complaint}</p>
                  </CardContent>
                </Card>
              )}

              {/* Symptoms */}
              {triageData.symptoms && triageData.symptoms.length > 0 && (
                <Card className="health-card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Activity className="w-5 h-5" />
                      Reported Symptoms
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {triageData.symptoms.map((symptom: string, idx: number) => (
                        <Badge key={idx} variant="outline">
                          {symptom.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                    {triageData.pain_severity > 0 && (
                      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm text-muted-foreground">Pain Severity: </span>
                        <span className="font-medium">{triageData.pain_severity}/10</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Vitals */}
              {triageData.vitals && Object.keys(triageData.vitals).some(k => triageData.vitals[k]) && (
                <Card className="health-card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Thermometer className="w-5 h-5" />
                      Vital Signs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {triageData.vitals.temperature && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Temperature</p>
                          <p className="font-semibold">{triageData.vitals.temperature}°F</p>
                        </div>
                      )}
                      {triageData.vitals.blood_pressure && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Blood Pressure</p>
                          <p className="font-semibold">{triageData.vitals.blood_pressure}</p>
                        </div>
                      )}
                      {triageData.vitals.heart_rate && (
                        <div className="text-center p-3 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">Heart Rate</p>
                          <p className="font-semibold">{triageData.vitals.heart_rate} bpm</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Medical History */}
              {triageData.medical_history && triageData.medical_history.length > 0 && (
                <Card className="health-card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Heart className="w-5 h-5" />
                      Medical History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {triageData.medical_history.map((condition: string, idx: number) => (
                        <Badge key={idx} variant="secondary">
                          {condition.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Medications & Allergies */}
              {((triageData.medications && triageData.medications.length > 0) || 
                (triageData.allergies && triageData.allergies.length > 0)) && (
                <Card className="health-card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Pill className="w-5 h-5" />
                      Medications & Allergies
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {triageData.medications && triageData.medications.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Current Medications</p>
                        <div className="flex flex-wrap gap-2">
                          {triageData.medications.map((med: string, idx: number) => (
                            <Badge key={idx} variant="outline">{med}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {triageData.allergies && triageData.allergies.length > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Known Allergies</p>
                        <div className="flex flex-wrap gap-2">
                          {triageData.allergies.map((allergy: string, idx: number) => (
                            <Badge key={idx} variant="destructive" className="bg-destructive/10 text-destructive border-destructive/20">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {allergy}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Emergency Symptoms */}
              {triageData.emergency_symptoms && triageData.emergency_symptoms.length > 0 && (
                <Card className="health-card mb-6 border-destructive/20">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg text-destructive">
                      <AlertTriangle className="w-5 h-5" />
                      Emergency Symptoms Reported
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {triageData.emergency_symptoms.map((symptom: string, idx: number) => (
                        <Badge key={idx} variant="destructive">
                          {symptom.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex gap-4 justify-center mt-8">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
            <Button onClick={() => navigate("/triage")}>
              Start New Assessment
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssessmentDetailPage;
